/**
 * STF Video Lightbox Patch
 * 在原有 shop-the-feed.js 末尾追加此文件内容
 * 或单独作为 stf-video-lightbox.js 加载（在 shop-the-feed.js 之后）
 *
 * 功能：
 * - 点击含视频的 stf-card → 弹出 lightbox，带音量控制
 * - 原有 hover 预览（静音inline播放）行为保持不变
 * - ESC键 / 点击遮罩 / 点击关闭按钮 → 关闭lightbox
 */

(function () {
  'use strict';

  // ─── 1. 创建 Lightbox DOM（单例，全局复用） ───────────────────────────────
  function createLightbox() {
    if (document.getElementById('stf-lightbox')) return;

    const overlay = document.createElement('div');
    overlay.id = 'stf-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Video player');
    overlay.innerHTML = `
      <div class="stf-lightbox__backdrop"></div>
      <div class="stf-lightbox__container">
        <button class="stf-lightbox__close" aria-label="Close video">&#x2715;</button>
        <div class="stf-lightbox__video-wrap">
          <video
            class="stf-lightbox__video"
            playsinline
            controls
            preload="auto"
            crossorigin="anonymous"
          ></video>
        </div>
        <div class="stf-lightbox__controls">
          <button class="stf-lightbox__mute-btn" aria-label="Toggle mute">
            <svg class="icon-sound-on" width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor"/>
              <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12z" fill="currentColor"/>
              <path d="M14 3.23v2.06A7 7 0 0 1 19 12a7 7 0 0 1-5 6.71v2.06A9 9 0 0 0 21 12 9 9 0 0 0 14 3.23z" fill="currentColor"/>
            </svg>
            <svg class="icon-sound-off" width="22" height="22" viewBox="0 0 24 24" fill="none" style="display:none">
              <path d="M3 9v6h4l5 5V4L7 9H3z" fill="currentColor"/>
              <path d="M17 14l-2-2 2-2-1.5-1.5L13 11l-2-2-1.5 1.5L11 13l-2 2 1.5 1.5L13 15l2 2L17 14z" fill="currentColor"/>
            </svg>
          </button>
          <div class="stf-lightbox__volume-wrap">
            <input
              type="range"
              class="stf-lightbox__volume"
              min="0"
              max="1"
              step="0.05"
              value="1"
              aria-label="Volume"
            />
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const video    = overlay.querySelector('.stf-lightbox__video');
    const backdrop = overlay.querySelector('.stf-lightbox__backdrop');
    const closeBtn = overlay.querySelector('.stf-lightbox__close');
    const muteBtn  = overlay.querySelector('.stf-lightbox__mute-btn');
    const volume   = overlay.querySelector('.stf-lightbox__volume');
    const iconOn   = muteBtn.querySelector('.icon-sound-on');
    const iconOff  = muteBtn.querySelector('.icon-sound-off');

    // 关闭
    function closeLightbox() {
      overlay.classList.remove('is-open');
      video.pause();
      video.src = '';
      document.body.style.overflow = '';
      // 把焦点还给触发元素
      if (overlay._triggerEl) {
        overlay._triggerEl.focus();
        overlay._triggerEl = null;
      }
    }

    backdrop.addEventListener('click', closeLightbox);
    closeBtn.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeLightbox();
    });

    // 音量滑块填充色同步（CSS变量）
    function syncVolumeFill() {
      const pct = parseFloat(volume.value) * 100;
      volume.style.setProperty('--volume-pct', pct);
    }

    // 音量滑块 ↔ 视频同步
    volume.addEventListener('input', () => {
      video.volume = parseFloat(volume.value);
      video.muted  = video.volume === 0;
      syncMuteIcon();
      syncVolumeFill();
    });

    muteBtn.addEventListener('click', () => {
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) {
        video.volume = 0.5;
        volume.value = 0.5;
      }
      syncMuteIcon();
    });

    video.addEventListener('volumechange', () => {
      volume.value = video.muted ? 0 : video.volume;
      syncMuteIcon();
    });

    function syncMuteIcon() {
      const muted = video.muted || video.volume === 0;
      iconOn.style.display  = muted ? 'none'  : '';
      iconOff.style.display = muted ? ''      : 'none';
    }

    return overlay;
  }

  // ─── 2. 打开 Lightbox ────────────────────────────────────────────────────
  function openLightbox(src, triggerEl) {
    const overlay = document.getElementById('stf-lightbox');
    if (!overlay) return;

    const video  = overlay.querySelector('.stf-lightbox__video');
    const volume = overlay.querySelector('.stf-lightbox__volume');

    overlay._triggerEl = triggerEl;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    overlay.querySelector('.stf-lightbox__close').focus();

    // 重置并加载新视频
    video.src    = src;
    video.volume = parseFloat(volume.value);
    video.muted  = false;
    video.load();
    video.play().catch(() => {});
  }

  // ─── 3. 为每个含视频的 stf-card 绑定点击事件 ────────────────────────────
  function bindCards() {
    document.querySelectorAll('stf-card').forEach((card) => {
      if (card._lightboxBound) return; // 防止重复绑定

      // 同时支持两种情况：
      // A) metafield视频：span.stf-video-src[data-src]
      // B) 原有block视频：video[src] 或 video-element
      const mediaWrap = card.querySelector('.stf-card__media');
      if (!mediaWrap) return;

      // 找视频URL来源
      const srcSpan  = card.querySelector('span.stf-video-src[data-src]');
      const videoEl  = card.querySelector('video');
      const src      = srcSpan?.dataset.src
                    || videoEl?.currentSrc
                    || videoEl?.src
                    || videoEl?.querySelector('source')?.src;
      if (!src) return;

      card._lightboxBound = true;

      mediaWrap.style.cursor = 'pointer';
      mediaWrap.setAttribute('role', 'button');
      mediaWrap.setAttribute('tabindex', '0');
      mediaWrap.setAttribute('aria-label', 'Play video in fullscreen');

      const handler = () => {
        if (src) {
          // 补全协议头（file_url 可能返回 // 开头的协议相对 URL）
          const fullSrc = src.startsWith('//') ? 'https:' + src : src;
          openLightbox(fullSrc, mediaWrap);
        }
      };

      mediaWrap.addEventListener('click', handler);
      mediaWrap.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler();
        }
      });
    });
  }

  // ─── 4. 初始化 ───────────────────────────────────────────────────────────
  function init() {
    createLightbox();
    bindCards();

    // Shopify Theme Editor 动态添加 block 时重新绑定
    document.addEventListener('shopify:block:select', () => setTimeout(bindCards, 300));
    document.addEventListener('shopify:section:load',  () => setTimeout(bindCards, 300));

    // 监听动态注入的 stf-card（Swiper懒加载场景）
    const observer = new MutationObserver(() => bindCards());
    const feed = document.querySelector('shop-the-feed');
    if (feed) observer.observe(feed, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();